import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import TRAIcon from "../icons/TRAIcon";
import { useThemeOptional } from "../theme/ThemeProvider";
import { useAuth } from "../../hooks/useAuth";
import styles from "./shell.module.css";
import { cn } from "../utils/cn";

/** Header profile / account menu + theme toggle. */
export default function UserProfileMenu({ className }) {
  const { isAuthenticated, user, signOut } = useAuth();
  const theme = useThemeOptional();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const label = user?.displayName || user?.email || "Account";

  return (
    <div className={cn(styles.menuWrap, className)} ref={wrapRef}>
      <button
        type="button"
        className={cn(styles.iconBtn, styles.headerIconBtn)}
        aria-label="Account menu"
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen((v) => !v)}
      >
        <TRAIcon name="user" size="md" />
      </button>
      {open ? (
        <div className={styles.menuPanel} role="menu" aria-label="Account">
          {isAuthenticated ? (
            <>
              <div className={styles.menuItem} style={{ cursor: "default", opacity: 0.8 }}>
                {label}
              </div>
              <Link
                role="menuitem"
                className={styles.menuItem}
                to="/profile"
                onClick={() => setOpen(false)}
              >
                <TRAIcon name="user" size="sm" />
                My Profile
              </Link>
              <button
                type="button"
                role="menuitem"
                className={styles.menuItem}
                onClick={() => {
                  const next = theme?.resolvedThemeId === "dark" ? "light" : "dark";
                  theme?.setMode?.(next);
                }}
              >
                <TRAIcon name={theme?.resolvedThemeId === "dark" ? "sun" : "moon"} size="sm" />
                {theme?.resolvedThemeId === "dark" ? "Light mode" : "Dark mode"}
              </button>
              <button
                type="button"
                role="menuitem"
                className={styles.menuItem}
                onClick={async () => {
                  setOpen(false);
                  await signOut();
                  navigate("/sign-in");
                }}
              >
                <TRAIcon name="signOut" size="sm" />
                Sign out
              </button>
            </>
          ) : (
            <>
              <Link
                role="menuitem"
                className={styles.menuItem}
                to="/sign-in"
                onClick={() => setOpen(false)}
              >
                Sign in
              </Link>
              <button
                type="button"
                role="menuitem"
                className={styles.menuItem}
                onClick={() => {
                  const next = theme?.resolvedThemeId === "dark" ? "light" : "dark";
                  theme?.setMode?.(next);
                }}
              >
                <TRAIcon name={theme?.resolvedThemeId === "dark" ? "sun" : "moon"} size="sm" />
                {theme?.resolvedThemeId === "dark" ? "Light mode" : "Dark mode"}
              </button>
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}
